import { StatusResource, requestMenuImport, updatePartner } from 'api'
import { Availabilities } from 'api/menuMapping/types'
import { LocationHourResource, MenuMappingSectionResource, TypeName } from 'api/models'
import { getLocationHours } from 'api/partners/locationHours'
import ellipse_2_green from 'assets/icons/ellipse-2-green.svg'
import ellipse_2_red from 'assets/icons/ellipse-2-red.svg'
import { AxiosError, isAxiosError } from 'axios'
import { Button, ErrorMessage, LoadingSkeleton, MenuImport, Section, Table, TableMobile, Modal } from 'components'
import ConnectionModal from 'components/ConnectionModal/ConnectionModal'
import { LocationUpcharge } from 'components/LocationUpcharge/LocationUpcharge'
import OrderThrottleToggle from 'components/OrderThrottling/OrderThrottleToggle'
import PosServiceFeeId from 'components/PosServiceFeeId/PosServiceFeeId'
import SectionHeader from 'components/Section/SectionHeader'
import SectionTitle from 'components/Section/SectionTitle'
import ServiceFee from 'components/ServiceFee/ServiceFee'
import SingleLocationDropdown from 'components/SingleLocationDropdown/SingleLocationDropdown'
import DigitalMarketing from 'features/connections/DigitalMarketing/DigitalMarketing'
import Platforms from 'features/connections/Platforms/Platforms'
import {
  useGetLocationPartners,
  useGetLocations,
  useGetMenuMappingSections,
  useIsMobile,
} from 'hooks'
import { useAuth0 } from 'libs/auth0-react'
import { sortBy, startCase } from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useRecoilState } from 'recoil'
import { upchargeState } from 'state/atoms'
import { Location } from 'types'
import { getMenuManagementNewURL, getMenuManagementURL, getMenuManagementPreviewURL } from 'utils'
import { isPosFeatureDisabled } from 'utils/PosConfig'
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HoursModal from 'components/HoursModal/HoursModal'
import MenuAvailabilityModal from '../IndividualMenu/MenuAvailability/MenuAvailabilityModal'
import { getMenuColumns } from './columns'
import * as Styled from './styles'
import AlertModal from 'components/AlertModal/AlertModal'
import { deleteMenuMappingSection, updateMenuMappingSection } from 'api/menuMapping/menus'

type FormValueTypes = {
  upcharge: number | string
}

const LOCATION_STATUS_STATE = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
}

// Todo : will use in future with added doordash features

// type MenuErrorTypes = {
//   details: string,
//   doordash_store_uuid: string,
//   exclusion_code: string,
//   location_id: string,
//   menus: [],
//   onboarding_id: string,
//   status: string
// }

const IndividualLocation = () => {
  const { user } = useAuth0()
  const { isMobile } = useIsMobile()
  const { locationId: urlLocationId } = useParams()

  const [searchParams] = useSearchParams()
  const { defaultLocation, setDefaultLocation, findAndSetDefaultLocation } = useGetLocations()
  const locationId = defaultLocation !== null ? String(defaultLocation?.id) : ''
  const marketingChannelIdParam = searchParams.get('marketingChannelId')
  const errorCallback = searchParams.get('error')

  const {
    partner,
    platforms,
    isLoading,
    isError, // permanent error on request
    menuMappingMenuId,
    organization,
    serviceFee,
    serviceFeeType,
    refetch,
  } = useGetLocationPartners(locationId)
  const { menuMappingSections: menuSections, isLoading: menuSectionsLoading, refetch: refetchMenuSections } =
    useGetMenuMappingSections(menuMappingMenuId)

  const navigate = useNavigate()

  const [upCharge, setUpcharge] = useRecoilState(upchargeState)
  // Todo : will use in future with added doordash features
  // const [doordashMenuError, setDoordashMenuError] = useState<MenuErrorTypes>()
  const [upchargeReadOnly, setUpchargeReadOnly] = useState(false)
  const [connectionModalOpen, setConnectionModalOpen] = useState(Boolean(marketingChannelIdParam))
  const [locationHours, setLocationHours] = useState<Availabilities[]>([])
  const [isMenuAvailabilityModalOpen, setIsMenuAvailabilityModalOpen] = useState(false)
  const posName = partner?.attributes?.pos?.toLowerCase() || ''
  const isPrinterClient = posName.includes('epson') // We don't want to show printer clients the menu resync option
  const isAdminUser = user?.email?.includes('@chowlyinc.com')
  // This "platform" check pertains to the customer's foor ordering platforms (i.e. Doordash, UberEats), not the Platform user
  const isMmrEnabled = partner?.attributes?.use_mmr

  const showMenuResync = !isPrinterClient || (isPrinterClient && isMmrEnabled)
  const isRCCPlatform = organization?.attributes?.chowly_platform

  const isToastPos = partner?.attributes?.pos?.toLowerCase() === 'toast'
  const showPosServiceFeeId = isAdminUser && isToastPos && isRCCPlatform

  // NEW
  const [isHoursModalOpen, setIsHoursModalOpen] = useState<boolean>(false)

  // Menu section modals state
  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedMenuSection, setSelectedMenuSection] = useState<MenuMappingSectionResource | null>(null)
  const [renameInput, setRenameInput] = useState('')

  // Handlers for kabob menu actions
  const handleViewMenu = (row: MenuMappingSectionResource) => {
    navigate(getMenuManagementPreviewURL(locationId, row.id))
  }
  const handleRenameMenu = (row: MenuMappingSectionResource) => {
    setSelectedMenuSection(row)
    setRenameInput(row.attributes.title)
    setRenameModalOpen(true)
  }
  const handleDeleteMenu = (row: MenuMappingSectionResource) => {
    setSelectedMenuSection(row)
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!selectedMenuSection) return
    try {
      await deleteMenuMappingSection(selectedMenuSection.id)
      toast.success(`Menu section "${selectedMenuSection.attributes.title}" was successfully deleted.`)
      await refetchMenuSections()
    } catch (err) {
      toast.error(`Failed to delete menu section "${selectedMenuSection.attributes.title}". Please try again.`)
    }
    setDeleteModalOpen(false)
    setSelectedMenuSection(null)
  }

  const handleConfirmRename = async () => {
    if (!selectedMenuSection) return
    try {
      await updateMenuMappingSection(selectedMenuSection.id, {
        data: {
          id: selectedMenuSection.id,
          type: TypeName.MenuMappingSection,
          attributes: { title: renameInput },
        },
      })
      toast.success(`Menu section renamed to "${renameInput}" successfully.`)
      await refetchMenuSections()
    } catch (err) {
      toast.error(`Failed to rename menu section to "${renameInput}". Please try again.`)
    }
    setRenameModalOpen(false)
    setSelectedMenuSection(null)
  }

  /**
   * Handle URL redirection for Google by setting the right Location info
   */
  useEffect(() => {
    if (urlLocationId && locationId && locationId !== urlLocationId) {
      findAndSetDefaultLocation(urlLocationId, getMenuManagementURL)
    }
  }, [urlLocationId, locationId])

  useEffect(() => {
    if (errorCallback) {
      toast.error('Error while integrating google business profile.')
    }
  }, [])

  // if the page is loaded without an id in the url param, set the default location as the id.
  useEffect(() => {
    if (defaultLocation && !urlLocationId) {
      navigate(getMenuManagementURL(defaultLocation.id))
    }
  }, [defaultLocation])

  useEffect(() => {
    const readOnly: boolean = isPosFeatureDisabled('upcharge', posName)
    setUpchargeReadOnly(readOnly)
  }, [posName])

  useEffect(() => {
    if (partner?.attributes.upcharge === null) {
      setUpcharge(0)
    } else {
      // @ts-ignore
      setUpcharge(partner?.attributes.upcharge)
    }
  }, [partner, locationId])

  const transformLocationHours = (data: LocationHourResource[]): Availabilities[] => {
    data.forEach((item) => {
      if (item.attributes.time_periods.length > 1) {
        item.attributes.time_periods = sortBy(item.attributes.time_periods, (timePeriod) =>
          moment(timePeriod.start_time, 'HH:mm').toDate(),
        )
      }
    })

    return data.map((item) => ({
      id: item.id,
      dayOfWeek: startCase(item.attributes.day_of_week),
      timePeriods: item.attributes.time_periods.map((period, index) => ({
        id: item.relationships.time_periods.data[index]?.id || '',
        attributes: {
          start_time: period.start_time,
          end_time: period.end_time,
        },
      })),
      hours: item.attributes.time_periods
        .map(
          (period) =>
            `${moment(period.start_time, 'HH:mm').format('h:mmA')} - ${moment(
              period.end_time,
              'HH:mm',
            ).format('h:mmA')}`,
        )
        .join(', ') as string,
      enabled: item.attributes.enabled,
    })) as Availabilities[]
  }

  const fetchLocationHours = useCallback(async () => {
    try {
      if (!locationId) return
      const response = await getLocationHours(locationId)
      setLocationHours(transformLocationHours(response?.data))
    } catch (error) {
      console.error('Failed to fetch store hours:', error)
    }
  }, [locationId])

  useEffect(() => {
    if (!locationId) return
    fetchLocationHours()
  }, [locationId, fetchLocationHours])

  const handleSubmit = async (value: FormValueTypes) => {
    if (locationId) {
      try {
        await updatePartner(locationId, { attributes: value })
        toast.success('Saved!')
        refetch()
      } catch (err) {
        toast.error(
          'Failed in saving the changes. Try again. For additional help please contact Chowly Support using the “C” icon in the lower right corner of your screen.',
        )
      }
    }
  }

  const reSyncSubmit = useCallback(async () => {
    if (menuMappingMenuId) {
      if (locationId) {
        try {
          const { included } = await requestMenuImport(locationId, false, true)
          if (included) {
            const status = included.find(
              (item): item is StatusResource =>
                item.type === 'status' && item.attributes.category === 'menu_import',
            )
            if (status) {
              // setMenuImportTimestamp(status.attributes.enqueued_at)
              // setEnqueued(status.attributes.enqueued_at ? true : false)
            }
          }
        } catch (err) {
          if (isAxiosError(err)) {
            if (err.response?.status === 429) {
              toast.error(
                'A menu resync was enqueued less than 5 minutes ago. Please wait at least 5 minutes before resyncing again. For additional help please contact Chowly Support using the “C” icon in the lower right corner of your screen.',
              )
            } else {
              toast.error(`Resync action couldn’t start due to ${(err as AxiosError).message}`)
            }
          } else {
            toast.error(`Unknown error: ${String(err)}`)
          }
        }
      }
    }
  }, [menuMappingMenuId, locationId])

  const menusColumns = useMemo(() => {
    if (!locationId) {
      return []
    }
    return getMenuColumns(locationId, handleViewMenu, handleRenameMenu, handleDeleteMenu, isPrinterClient)
  }, [locationId, isPrinterClient, reSyncSubmit])

  const hasCOOenabled = platforms?.find((p) => p.name === 'Chowly Online Ordering')
  const isOrderThrottlingEnabled = partner?.attributes.price_based_prep_times

  // Handle location change from the dropdown
  const handleLocationChange = (location: Location) => {
    setDefaultLocation({
      id: location.id,
      name: location.name,
      organization_id: location.organization_id,
      organization_name: location.organization_name,
      timezone: location.timezone,
    })

    // Update the locationId on URL
    navigate(getMenuManagementURL(location?.id))
  }

  const TableComponent = isMobile ? TableMobile : Table

  return (
    <Styled.LocationContainer>
      <SingleLocationDropdown onLocationChange={handleLocationChange} />
      {isError && !partner && <ErrorMessage />}
      {(partner || isLoading) && (
        <>
          <Styled.LocationHeader>
            <Styled.StatusContainer width={'8%'}>
              {isLoading ? (
                <LoadingSkeleton isFlex />
              ) : (
                <>
                  <Styled.Icon
                    src={
                      partner?.attributes?.aasm_state === LOCATION_STATUS_STATE.ENABLED
                        ? ellipse_2_green
                        : ellipse_2_red
                    }
                    alt='ellipseIcon'
                  />
                  <Styled.StatusText
                    isSuccess={partner?.attributes?.aasm_state === LOCATION_STATUS_STATE.ENABLED}
                    data-testid='location-status'
                  >
                    {partner?.attributes?.aasm_state}
                  </Styled.StatusText>
                </>
              )}
            </Styled.StatusContainer>
          </Styled.LocationHeader>

          <Styled.LocationHoursContainer>
            <Button 
              onClick={() => setIsHoursModalOpen(true)}
              size='small'
              narrow
              startIcon={<AccessTimeIcon />}
            >
              Hours
            </Button>
          </Styled.LocationHoursContainer>

          <Styled.Grid>
            {!upchargeReadOnly && (
              <Styled.LocationFormItem width={isMobile ? '100%' : null}>
                <Styled.LocationConnectionContainer>
                  <LocationUpcharge
                    onSubmit={handleSubmit}
                    currentUpcharge={upCharge}
                    isLoading={isLoading}
                  />
                </Styled.LocationConnectionContainer>
              </Styled.LocationFormItem>
            )}
            {showMenuResync && (
              <Styled.LocationFormItem upchargeReadOnly={upchargeReadOnly}>
                <Styled.LocationConnectionContainer>
                  <MenuImport
                    upCharge={upCharge}
                    resyncSumbit={reSyncSubmit}
                    onSubmit={handleSubmit}
                    locationId={parseInt(locationId || '0')}
                    isPrinterClient={isPrinterClient}
                  />
                </Styled.LocationConnectionContainer>
              </Styled.LocationFormItem>
            )}

            {hasCOOenabled && isAdminUser && isRCCPlatform && (
              <Styled.LocationFormItem style={{ height: 'auto' }}>
                <Styled.LocationConnectionContainer>
                  <ServiceFee
                    locationId={locationId}
                    platformId={hasCOOenabled.id}
                    refetch={refetch}
                    serviceFee={serviceFee}
                    serviceFeeType={serviceFeeType}
                  />
                </Styled.LocationConnectionContainer>
              </Styled.LocationFormItem>
            )}
            {showPosServiceFeeId && (
              <Styled.LocationFormItem style={{ height: 'auto' }}>
                <Styled.LocationConnectionContainer>
                  <PosServiceFeeId
                    locationId={locationId}
                    attributes={partner?.attributes}
                    posServiceFeeId={partner?.attributes?.pos_service_charge_id}
                    refetch={refetch}
                  />
                </Styled.LocationConnectionContainer>
              </Styled.LocationFormItem>
            )}
          </Styled.Grid>

          {/* Tools Section */}
          {hasCOOenabled ? (
            <OrderThrottleToggle
              partnerId={partner?.id}
              attributes={partner?.attributes}
              isOrderThrottlingEnabled={isOrderThrottlingEnabled}
            />
          ) : null}

          {/* Platforms section */}
          {partner && (
            <Platforms
              partner={partner}
              locationPlatforms={platforms}
              isLoading={isLoading}
              refetchLocationPartners={refetch}
            />
          )}
        </>
      )}

      {partner && (
        <DigitalMarketing
          location={partner}
          organization={organization}
          isLoading={isLoading}
          sectionTitle='Connections'
          locationName={defaultLocation?.name || ''}
        />
      )}

      {(menuSections || menuSectionsLoading) && (
        <Section>
          <SectionHeader>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <div style={{ flex: 1 }}>
                <SectionTitle>Menus</SectionTitle>
              </div>
              {isPrinterClient && (
                <Button
                  size='small'
                  narrow
                  onClick={() => navigate(getMenuManagementNewURL(locationId))}>
                  Add Menu
                </Button>
              )}
            </div>
          </SectionHeader>
          <TableComponent
            path={'./styles.ts'}
            columns={menusColumns}
            rowKey='id'
            data={menuSections ?? []}
            isLoading={menuSectionsLoading}
          />
        </Section>
      )}

      {marketingChannelIdParam && locationId && (
        <ConnectionModal
          isOpen={connectionModalOpen}
          locationName={defaultLocation?.name || ''}
          onClose={() => setConnectionModalOpen(false)}
          showInfoIcon={false}
          marketingChannelId={marketingChannelIdParam}
          locationId={locationId}
        />
      )}

      {/* <MenuResyncRequiredBanner /> */}
      {isMenuAvailabilityModalOpen && locationHours && (
        <MenuAvailabilityModal
          isOpen={isMenuAvailabilityModalOpen}
          onClose={() => setIsMenuAvailabilityModalOpen(false)}
          availabilities={locationHours}
          refetchData={fetchLocationHours}
          type='location_hour'
          partnerId={locationId || ''}
        />
      )}

      {isHoursModalOpen && locationId && (
        <HoursModal
          partnerId={locationId}
          isOpen={isHoursModalOpen}
          onClose={() => setIsHoursModalOpen(false)}
        />
      )}

      {/* Rename Modal */}
      {renameModalOpen && selectedMenuSection && (
        <Modal isOpen={renameModalOpen} cardProps={{ borderRadius: 8 }}>
          <div style={{ padding: 24, minWidth: 320 }}>
            <h2 style={{ marginBottom: 16, fontWeight: 600 }}>Rename {selectedMenuSection.attributes.title}</h2>
            <input
              type="text"
              value={renameInput}
              onChange={e => setRenameInput(e.target.value)}
              style={{ width: '100%', marginBottom: 4, padding: 8, fontSize: 16 }}
            />
            <Styled.RenameInputLabel>Enter the new name you want for this menu</Styled.RenameInputLabel>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="textonly" onClick={() => setRenameModalOpen(false)}>Cancel</Button>
              <Button onClick={handleConfirmRename}>Apply</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && selectedMenuSection && (
        <AlertModal
          isOpen={deleteModalOpen}
          title={`Are you sure you want to delete "${selectedMenuSection.attributes.title}"?`}
          message={"This will also delete Menu Items and Modifiers under this Menu."}
          buttonTitle="Yes"
          showInfoIcon={false}
          onClose={() => setDeleteModalOpen(false)}
          buttonAction={handleConfirmDelete}
        />
      )}
    </Styled.LocationContainer>
  )
}

export default IndividualLocation
